/* Read-only color previews. OwnPrint and Dwell retain all option, upload and cart handling. */
(() => {
  if (customElements.get('shunova-color-preview')) return;
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const coordinates = {
    fingerprint: { gold: [277, 309, 19.8], silver: [277.5, 308, 21.7], 'rose-gold': [277.5, 308, 21.7] },
    vertical: { gold: [302, 194, 16.4], silver: [302.5, 194, 16.6], 'rose-gold': [302.5, 194, 16.6] },
    'photo-heart': { gold: [325, 223, 17.6], silver: [325, 222, 19.7], 'rose-gold': [325, 222, 19.7] }
  };
  let serial = 0;
  const finishKey = value => /rose/i.test(value) ? 'rose-gold' : /silver/i.test(value) ? 'silver' : /gold/i.test(value) ? 'gold' : null;
  class ColorPreview extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try { this.config = JSON.parse(this.querySelector('[data-preview-config]').textContent); } catch { return; }
      this.gallery = this.closest('media-gallery');
      this.scope = this.closest('.shopify-section') || this.closest('dialog') || this.parentElement;
      if (!this.gallery || !this.scope) return;
      this.controller = new AbortController();
      this.base = this.querySelector('[data-preview-base]');
      this.stone = this.querySelector('[data-preview-stone]');
      this.base.addEventListener('error', () => {
        this.hidden = true;
        delete this.gallery.dataset.shunovaView;
      }, { signal: this.controller.signal });
      const clipId = `shunova-stone-${++serial}`;
      this.stone.querySelector('clipPath').id = clipId;
      this.stone.querySelector('g').setAttribute('clip-path', `url(#${clipId})`);
      this.stone.querySelector('image').setAttribute('href', this.config.stoneChart);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-preview-view]');
        if (button) this.setView(button.dataset.previewView);
      }, { signal: this.controller.signal });
      this.scope.addEventListener('change', event => {
        if (!this.contains(event.target)) this.queueUpdate(true);
      }, { signal: this.controller.signal });
      this.scope.addEventListener('click', event => {
        if (event.target.closest('[data-ownprint-swatch], [data-ownprint-tile], variant-picker')) this.queueUpdate(true);
      }, { signal: this.controller.signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target) && !record.target.closest?.('media-gallery'))) this.queueUpdate(false);
      });
      this.observer.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-checked', 'checked', 'selected', 'value'] });
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    queueUpdate(showPreview) {
      this.showOnUpdate ||= showPreview;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.update(this.showOnUpdate);
        this.showOnUpdate = false;
      });
    }
    selected(name) {
      // OwnPrint may initialize to a different selection from the hidden native picker.
      for (const group of this.scope.querySelectorAll('[role="radiogroup"][aria-label]')) {
        if (group.getAttribute('aria-label').toLowerCase() !== name) continue;
        const checked = group.querySelector('[aria-checked="true"]');
        if (checked) return checked.dataset.ownprintSwatch || checked.dataset.ownprintTile || checked.getAttribute('aria-label') || checked.textContent.trim();
      }
      for (const input of this.scope.querySelectorAll('[data-ownprint-option-name]')) {
        if (input.dataset.ownprintOptionName.toLowerCase() === name && input.value) return input.value;
      }
      for (const select of this.scope.querySelectorAll('variant-picker select')) {
        if (select.name.toLowerCase() === `options[${name}]`) return select.value;
      }
      const checked = [...this.scope.querySelectorAll('variant-picker input:checked')].find(input => input.dataset.optionName?.toLowerCase() === name);
      if (checked) return checked.value;
      const option = this.config.options.find(option => option.name.toLowerCase() === name);
      return option?.selected_value || '';
    }
    setView(view) {
      this.gallery.dataset.shunovaView = view;
      this.querySelector('.shunova-preview-figure').hidden = view !== 'preview';
      this.querySelectorAll('[data-preview-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.previewView === view)));
    }
    update(showPreview = false) {
      const finish = finishKey(this.selected('plating'));
      const band = this.dataset.productKey === 'bracelet' ? (/black/i.test(this.selected('band')) ? 'black' : 'brown') : null;
      const key = band ? `${finish}-${band}` : finish;
      const source = this.config.images[key];
      if (!source) { this.hidden = true; delete this.gallery.dataset.shunovaView; return; }
      const month = this.selected('birthstone');
      const index = months.findIndex(value => value.toLowerCase() === month.toLowerCase());
      const position = coordinates[this.dataset.productKey]?.[finish];
      // A missing or unsupported month must never display the base photo's yellow stone as the selected color.
      if (position && index < 0) { this.hidden = true; delete this.gallery.dataset.shunovaView; return; }
      const finishLabel = finish === 'rose-gold' ? 'Rose gold' : finish === 'silver' ? 'Silver' : 'Gold';
      const caption = [finishLabel, band ? `${band === 'black' ? 'Black' : 'Brown'} leather` : null, position ? `${months[index]} birthstone` : null].filter(Boolean).join(' · ');
      this.base.alt = `${this.dataset.productTitle} — ${caption}, digital color preview`;
      if (this.base.getAttribute('src') !== source) this.base.src = source;
      const captionElement = this.querySelector('[data-preview-caption]');
      if (captionElement.textContent !== caption) captionElement.textContent = caption;
      this.stone.toggleAttribute('hidden', !position);
      if (position) {
        const [x, y, radius] = position;
        const circle = this.stone.querySelector('circle');
        circle.setAttribute('cx', x); circle.setAttribute('cy', y); circle.setAttribute('r', radius);
        const chartX = [96, 242, 388, 534][index % 4];
        const chartY = [126, 326, 510][Math.floor(index / 4)];
        const scale = radius / 25.5;
        const image = this.stone.querySelector('image');
        image.setAttribute('x', x - chartX * scale);
        image.setAttribute('y', y - chartY * scale);
        image.setAttribute('width', 640 * scale);
        image.setAttribute('height', 640 * scale);
      }
      this.hidden = false;
      if (!this.gallery.dataset.shunovaView || showPreview) this.setView('preview');
    }
  }
  customElements.define('shunova-color-preview', ColorPreview);
})();
